import { HTML5toTouch } from "rdndmb-html5-to-touch";
import { useEffect, useRef, useState } from "react";
import { DndProvider } from "react-dnd-multi-backend";
import Box from "../components/Box";
import Coin from "../components/Coin";
import { DraggableMessage } from "../components/Draggables";
import InputBox from "../components/InputBox";
import {
  COLOR_DIVISOR,
  COLOR_HIGHLIGHTED,
  FONT,
  ROMAN_FONT,
  ROMAN_SYSTEM
} from "../defaults";
import { convertToAlternate, countRomanCharOccurrences, increaseProgress } from "../helpers";
import coin_C from "../images/coin_C.png";
import coin_I from "../images/coin_I.png";
import coin_L from "../images/coin_L.png";
import coin_V from "../images/coin_V.png";
import coin_X from "../images/coin_X.png";
import {
  buttonCheck,
  level2BinaryContinueDistributing,
  level2RomanDropLargerFirst,
  levelDivisionTask1,
  levelDivisionTask2,
  levelExchange,
  levelExchangeTooSmall,
  levelExchangeCoins,
  levelNotEnoughCoins,
  levelExchangeTooMany,
  levelDivisionTaskBoxes,
  levelWrongBox,
  levelWrongDistributed,
  levelWrongNotDistributed,
  levelCorrect,
  levelNoInputMulti
} from "../strings";


const LevelNumber = 2;

function calculateDividend(currentProgress) {
  const numbers = [27, 31, 9];
  if (currentProgress >= numbers.length) {
    return Math.round(Math.random() * 44) + 5; // Range 5-49
  }
  return numbers[currentProgress];
}

function calculateDivisor(currentProgress) {
  const numbers = [2, 3, 2];
  if (currentProgress >= numbers.length) {
    return Math.round(Math.random() * 1) + 2; // Range 2-3
  }
  return numbers[currentProgress];
}

const coinImages = {
  0: coin_C,
  1: coin_L,
  2: coin_X,
  3: coin_V,
  4: coin_I,
};

function Level2Roman({levelProgress, setLevelProgress, showMessage, setShowMessage, disableInput, setDisableInput}) {
  const [dividend, setDividend] = useState(
    calculateDividend(levelProgress[LevelNumber])
  );
  const [divisor, setDivisor] = useState(
    calculateDivisor(levelProgress[LevelNumber])
  );
  const [coins, setCoins] = useState([]); // How many coins to render on the left side. Only updated initially and then every time an exchange happens (in handleExchange). Not updated upon dropping a coin. This might change in the future.
  const [result, setResult] = useState([]); // How many coins should be in each divisor box at the end of the game.
  const [droppedCoins, setDroppedCoins] = useState([0, 0, 0, 0, 0]); // How many coins of each type have been dropped so far. Used for measuring progress. If droppedCoins[i] === coins[i], this means all coins of type i have been dropped. 
  const [coinsInBoxes, setCoinsInBoxes] = useState( // How many coins have been distributed so far in each divisor box. Overlaps a little bit with droppedCoins, I think.
    Array.from({ length: 5 }, () => [0, 0, 0, 0, 0]) 
  );
  const [exchanged, setExchanged] = useState([false,false,false,false,false]); // Whether a certain coin type has been exchanged (for another). If this is true, then no coins of that type are left over. 
  const [distributionState, setDistributionState] = useState(0); // State variable with divisor amount of states (2 or 3). Corresponds to boxIndex. (0 - coins equally distributed (default), 1 - first box has just gotten a coin. If divisor===3: 2 - second box has just gotten a coin (if divisor===2 this is equivalent to state 0, hence not used)) 
  const [message, setMessage] = useState("");
  const [correct, setCorrect] = useState(false);


  useEffect(() => {
    let divd = calculateDividend(levelProgress[LevelNumber]);
    let divr = calculateDivisor(levelProgress[LevelNumber]);
    let sum = Array.from(
      String(Math.floor(divd / divr)).padStart(3, "0"),
      Number
    ).reduce((partialSum, a) => partialSum + a, 0);
    while (sum * divr > 20) {
      divd = calculateDividend(levelProgress[LevelNumber]);
      divr = calculateDivisor(levelProgress[LevelNumber]);
      sum = Array.from(
        String(Math.floor(divd / divr)).padStart(3, "0"),
        Number
      ).reduce((partialSum, a) => partialSum + a, 0);
    }
    setDividend(divd);
    setDivisor(divr);
  }, [levelProgress]);

  useEffect(() => { // Initialization, executed each time a new exercise (new dividend & divisor) is loaded.
    let romanString = convertToAlternate(ROMAN_SYSTEM, dividend);
    let charOccurrences = countRomanCharOccurrences(romanString); 
    let frequencyArray = Array.from({ length: 5 }); 
    for (const [char, frequency] of Object.entries(charOccurrences)) { // LLM-generated this line (loop structure) 
      switch (char) {
        case 'I': frequencyArray[4] = frequency; break;
        case 'V': frequencyArray[3] = frequency; break;
        case 'X': frequencyArray[2] = frequency; break;
        case 'L': frequencyArray[1] = frequency; break;
        case 'C': frequencyArray[0] = frequency; break;
      }
    }

    let resultString = convertToAlternate(ROMAN_SYSTEM, Math.floor(dividend / divisor));
    let resultOccurrences = countRomanCharOccurrences(resultString); 
    let resultArray = Array.from({ length: 5 }); 
    for (const [char, frequency] of Object.entries(resultOccurrences)) { // LLM-generated this line (loop structure) 
      switch (char) {
        case 'I': resultArray[4] = frequency; break;
        case 'V': resultArray[3] = frequency; break;
        case 'X': resultArray[2] = frequency; break;
        case 'L': resultArray[1] = frequency; break;
        case 'C': resultArray[0] = frequency; break;
      }
    }
  
    setExchanged([false,false,false,false,false]);
    setCoins(frequencyArray);
    setResult(resultArray); // E.g. if result is VIII, this is [0,0,0,1,3]
    setDroppedCoins([0, 0, 0, 0, 0]); 
    setCoinsInBoxes(Array.from({ length: 5 }, () => [0, 0, 0, 0, 0])); 
    setDistributionState(0); // Reset state.
  }, [dividend, divisor]);

  function handleDropCoin(item, boxIndex) { // item[0] is index that corresponds to coin type.
    const allBoxesFilled = coinsInBoxes
      .slice(0, divisor)
      .every((box) => box[item[0]] >= result[item[0]]);
    
    for (let i = 0; i < item[0]; ++i) { // Loop to check all indices smaller than item[0]. Checks if any larger coin types still exist. If they do, drop is not permitted.
      let someCoinsLeft = !exchanged[i] && coins[i] !== droppedCoins[i]; // Check if for this larger coin type i some coins are left. Some coins are left iff the coins were not exchanged and not all were dropped.
      if (someCoinsLeft) { // If for any of the larger coin types some coins still exist, then user is not allowed to drop the coin they just tried to drop.
        setMessage(level2RomanDropLargerFirst);
        displayMessage();
        return; // Exit function
      }
    }
  
    if (allBoxesFilled && item[0] === 4) { 
      setMessage(levelNotEnoughCoins);
      displayMessage();
    } else if (allBoxesFilled) {
      setMessage(levelExchangeCoins);
      displayMessage();
    } else if (coinsInBoxes[boxIndex][item[0]] >= result[item[0]]) {
      setMessage(levelWrongBox);
      displayMessage();
    } else {
      const newDroppedCoins = [...droppedCoins];
      newDroppedCoins[item[0]] += 1;
      setDroppedCoins(newDroppedCoins);
      const newCoinsInBoxes = [...coinsInBoxes];
      newCoinsInBoxes[boxIndex][item[0]] += 1;
      setCoinsInBoxes(newCoinsInBoxes);
      setDistributionState(n => ((n+1) % divisor)); // Increment distribution state
    }
  }

  // Hint: Smaller index means larger coin type.
  function handleExchange(index) {
    if (index === 4) { // Smallest coin cannot be exchanged. 
      setMessage(levelExchangeTooSmall);
      displayMessage();
    }
    if (distributionState > 0) { // Case: Exchange unsuccessful, not all boxes have equal amount of coins.
      setMessage(level2BinaryContinueDistributing);
      displayMessage();
      return;
    }
    for (let i = 0; i < index; ++i) { // Loop to check all indices smaller than index. Checks if any larger coin types still exist. If they do, exchange is not permitted.
      let someCoinsLeft = !exchanged[i] && coins[i] !== droppedCoins[i]; // Check if for this larger coin type i some coins are left. Some coins are left iff the coins were not exchanged and not all were dropped.
      if (someCoinsLeft) { // If for any of the larger coin types some coins still exist, then user is not allowed to exchange the coin type they just tried to exchange.
        setMessage(level2RomanDropLargerFirst);
        displayMessage();
        return; // Exit function
      }
    }
    if (coins[index] - droppedCoins[index] < divisor) {
      const newCoins = [...coins];
      if ([0,2].includes(index)) { // Index corresponds to coin type C or X, which are converted to L and V respectively. 
        newCoins[index] = droppedCoins[index];
        newCoins[index + 1] += (coins[index] - droppedCoins[index]) * 2; 
      } else if ([1,3].includes(index)) { // Index corresponds to coin type L or V, which are converted to X and I respectively. 
        newCoins[index] = droppedCoins[index];
        newCoins[index + 1] += (coins[index] - droppedCoins[index]) * 5; 
      }
      const newExchanged = [...exchanged];
      newExchanged[index] = true;
      setExchanged(newExchanged);
      setCoins(newCoins); // IMPORTANT: coins is only updated upon exchange! droppedCoins, however, is updated continuously (every drop).
    } else {
      setMessage(levelExchangeTooMany);
      displayMessage();
    }
  }

  const handleCorrect = () => {
    setCorrect(true);
    setMessage(levelCorrect)
    displayMessage();
  };

  const handleIncorrect = () => {
    let allBoxesFilled = coinsInBoxes
      .slice(0, divisor)
      .every((box) => box[0] >= result[0]);
    allBoxesFilled =
      allBoxesFilled &&
      coinsInBoxes.slice(0, divisor).every((box) => box[1] >= result[1]);
    allBoxesFilled =
      allBoxesFilled &&
      coinsInBoxes.slice(0, divisor).every((box) => box[2] >= result[2]);
    allBoxesFilled =
      allBoxesFilled &&
      coinsInBoxes.slice(0, divisor).every((box) => box[3] >= result[3]); // Added cases 3 and 4. These ok? 
    allBoxesFilled =
      allBoxesFilled &&
      coinsInBoxes.slice(0, divisor).every((box) => box[4] >= result[4]);
    if (allBoxesFilled) {
      setMessage(levelWrongDistributed);
    } else {
      setMessage(levelWrongNotDistributed);
    }
    displayMessage();
  };

  const handleNoInput = () => {
    setMessage(levelNoInputMulti);
    displayMessage();
  };

  const handleCloseMessage = () => {
    if(correct) { // Increase Progress only upon closing popup message.
      increaseProgress(levelProgress, setLevelProgress, LevelNumber, ROMAN_SYSTEM);
    }
    setCorrect(false);
    setDisableInput(false);
  };

  const displayMessage = () => {
    setDisableInput(true);
    setShowMessage(true);
  };

  
  const containerArrayWidth = 70; // Used to scale the sizes of containers and coins.
  let coinSize = 6; // Size of the coins in dvh. Parametrized so that coinsStyle can be exactly 4 times as wide.

  const exchangeStyle = {
    padding: "1dvh",
    marginBottom: "2dvh",
    width: "10dvh",
    fontFamily: FONT,
    color: "white",
    fontSize: "1.6dvh",
    textShadow: "0.3svh 0.3svh 0.6svh rgba(0, 0, 0, 0.5)",
  };

  const containerStyle = {
    display: "flex",
    flexDirection: "column",
    flexWrap: "wrap",
    maxWidth: "40dvh", // Four times max coin width.
    maxHeight: "50dvh",
    alignItems: "center",
    justifyContent: "center",
  };

 
  const containerArrayStyle = {
    display: "flex", 
    flexDirection: "row", 
    marginRight: "25dvh", 
    justifyContent: "center",
    columnGap: "4dvh", 
    maxWidth: 1.5*containerArrayWidth+"dvh",
  };

  const coinsStyle = {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
    maxHeight: "50dvh",
    maxWidth: containerStyle.maxWidth, // This sets the max-width for the coin containers. Note that there may be a max of 4 Roman coins of same value. Hence EXACTLY 4 coins need to fit in this (for initial representation), the rest are put on another line.
    justifyContent: "center",
  };

  function RenderContainerArray() {
    let containerArray = [];
    let numberOfCoinsLeftSide = 0; // Updates only upon exchange, not upon drop. Hence it is an estimation. Used to scale the coins proportional to how many coins there are on screen.
    for (let i = 0; i < coins.length; ++i) {
      numberOfCoinsLeftSide += coins[i];
    }
    coinSize = (containerArrayWidth/numberOfCoinsLeftSide);
    for (let id = 0; id < coins.length; ++id) { // Render all coins for each coin type.
      if (coins[id] === droppedCoins[id]) continue; // Skip rest of the code if no coins will be rendered anyway.
      containerArray.push( // Individual container code starts here.
        <div style={containerStyle/*marginBottom: (3.5+parseFloat(exchangeStyle.marginBottom))+"dvh"*/ /*Cheat to have it centered. At marginBottom 3.5dvh it would be centered, but since exchangeStyle also has marginBottom, need to add it.*/}> 
          <button
            onClick={() => handleExchange(id)}
            style={exchangeStyle}
            className="exchange-button"
            disabled={disableInput}
          >
            {levelExchange}
          </button>
          <div style={coinsStyle}>
            {Array.from({ length: coins[id] }).map(
              (_, idx) =>
                droppedCoins[id] <= idx && (
                  <span style={{width: (coinSize)+"dvh", maxWidth: "10dvh", minWidth: "5dvh"}}><Coin
                    key={[id, idx]}
                    id={[id, idx]}
                    coinImage={coinImages[id]}
                  /></span>
                )
            )}
          </div>
        </div>
      );
    }
    return containerArray;
  }
 

  const inputBoxRef = useRef();

  // LLM-generated function.
  const callCheckInput = () => {
    // Call the handleAction function in the child via the ref
    if (inputBoxRef.current) {
      inputBoxRef.current.checkInput();
    }
  };

  return (
    <div>
      <div
        className="Welcome-label"
        style={{ fontFamily: FONT, fontSize: "3.5dvh" }}
      >
        {levelDivisionTask1}
        <span style={{fontFamily: ROMAN_FONT /**/, color: COLOR_HIGHLIGHTED, fontSize: "5dvh"}}>{convertToAlternate(ROMAN_SYSTEM, dividend)}</span>
        {levelDivisionTask2}
        <span style={{ color: COLOR_DIVISOR }}>{divisor}</span>?<br></br>
        {levelDivisionTaskBoxes}
      </div>
      
      <DndProvider options={HTML5toTouch}>
        {showMessage && <DraggableMessage show={showMessage} setShow={setShowMessage} handleClose={handleCloseMessage} message={message}/>} {/* Conditionally rendering it with showMessage makes the message re-render, and hence center itself, every time. */}
        <div style={{ display: "flex", flexDirection: "column"}}>
          <div style={{display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center", height: "30dvh"}}>
            <span style={containerArrayStyle}><RenderContainerArray/></span>
            <div style={{position: "absolute", top: "20%", left: "72%", /*Where the boxes are on screen.*/}}>
              {Array.from({ length: divisor }).map((_, idx) => (
                <Box
                  id={idx}
                  onDropCoin={handleDropCoin}
                  coins={coinsInBoxes[idx]} 
                  coinSize={6}
                />
              ))}
            </div>
          </div>
          <div style={{ display: "flex", marginTop: "3dvh", marginLeft: "auto", marginRight: "auto", paddingRight: containerArrayStyle.marginRight/*Padding same as containerArray has marginRight, so they align again.*/, justifyContent: "center"}}>
            <InputBox
              handleCorrect={handleCorrect}
              handleIncorrect={handleIncorrect}
              handleNoInput = {handleNoInput}
              dividend={dividend}
              divisor={divisor}
              remainder={dividend % divisor}
              whichSystem={ROMAN_SYSTEM}  
              showMessage={showMessage}
              disableInput={disableInput}
              ref={inputBoxRef}
            ></InputBox>
          </div>
          <button
            onClick={callCheckInput}
            className="checkAnswer-button"
            disabled={disableInput}
          >
          {buttonCheck}
          </button>
        </div>
      </DndProvider>
    </div>
  );
}

export default Level2Roman;
